import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltCheckbox } from '@cae/cobalt-react/components/checkbox';
import { CobaltDivider } from '@cae/cobalt-react/components/divider';
import { CobaltSearchField } from '@cae/cobalt-react/components/search-field';
import * as React from 'react';

import type { CheckboxFilterSectionProps } from './CheckboxFilterSection.types';

import styles from './CheckboxFilterSection.module.css';
import {
  filterItemsBySearch,
  getVisibleItems,
  DEFAULT_SEARCH_THRESHOLD,
  DEFAULT_SHOW_MORE_THRESHOLD,
} from './CheckboxFilterSection.utils';

/**
 * Reusable checkbox filter section component with optional search and "show more" functionality.
 *
 * Used in UsersFilterPanel for both Roles and Status filters, but designed to be flexible for other use cases.
 *
 * Features:
 * - Displays a list of options with checkboxes, allowing multiple selection
 * - Optional search input for filtering options (shown if `searchable` and options exceed `searchThreshold`)
 * - "Show more/less" toggle if options exceed `showMoreThreshold`
 * - Displays count of selected items in the header
 * - Accessible with proper ARIA attributes and keyboard support
 */
const CheckboxFilterSection: React.FC<CheckboxFilterSectionProps> = React.memo(
  ({
    label,
    headingId,
    dataTestId,
    options,
    selected,
    onSelect,
    searchable = false,
    searchThreshold = DEFAULT_SEARCH_THRESHOLD,
    showMoreThreshold = DEFAULT_SHOW_MORE_THRESHOLD,
    hasDivider = true,
  }): React.ReactElement => {
    const [search, setSearch] = React.useState<string>('');
    const [showAll, setShowAll] = React.useState<boolean>(false);

    const filteredOptions = React.useMemo(
      () => filterItemsBySearch(options, search),
      [options, search]
    );

    const visibleOptions = React.useMemo(
      () => getVisibleItems(filteredOptions, showAll, showMoreThreshold),
      [filteredOptions, showAll, showMoreThreshold]
    );

    const shouldShowSearch = React.useMemo(
      () => searchable && options.length > searchThreshold,
      [searchable, options.length, searchThreshold]
    );

    const hasMoreOptions = React.useMemo(
      () => filteredOptions.length > showMoreThreshold,
      [filteredOptions.length, showMoreThreshold]
    );

    const selectedCount = React.useMemo(
      () => selected.length,
      [selected.length]
    );

    const selectedSet = React.useMemo(() => new Set(selected), [selected]);

    const handleSearchChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearch(event.target.value);
      },
      []
    );

    const handleSearchClear = React.useCallback((): void => {
      setSearch('');
    }, []);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent): void => {
        if (event.key === 'Escape') {
          setSearch('');
        }
      },
      []
    );

    const handleToggleShowAll = React.useCallback((): void => {
      setShowAll(prev => !prev);
    }, []);

    const handleItemSelect = React.useCallback(
      (itemId: string) => (): void => {
        onSelect(itemId);
      },
      [onSelect]
    );

    React.useEffect(() => {
      if (search) {
        setShowAll(false);
      }
    }, [search]);

    const labelLower = React.useMemo(() => label.toLowerCase(), [label]);

    return (
      <section
        className={styles.checkboxFilterSection}
        data-testid={dataTestId}
        aria-labelledby={headingId}
        role="group"
      >
        {hasDivider && (
          <CobaltDivider
            color="subtle"
            className={styles.checkboxFilterSectionDivider}
          />
        )}

        <header className={styles.checkboxFilterSectionHeader}>
          <h4
            id={headingId}
            className={`body-02 ${styles.checkboxFilterSectionHeading}`}
          >
            {label}
          </h4>
          {selectedCount > 0 && (
            <span
              className="body-01"
              aria-live="polite"
              aria-label={`${selectedCount} ${labelLower} selected`}
            >
              ({selectedCount} selected)
            </span>
          )}
        </header>

        {shouldShowSearch && (
          <CobaltSearchField
            className={styles.checkboxFilterSectionSearch}
            placeholder={`Search ${labelLower}`}
            value={search}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            hasClear
            onClear={handleSearchClear}
            aria-label={`Search ${labelLower}`}
          />
        )}

        {visibleOptions.length > 0 ? (
          <div
            className={styles.checkboxFilterSectionList}
            role="group"
            aria-labelledby={headingId}
            id={`${headingId}-checkbox-group`}
          >
            {visibleOptions.map(option => (
              <CobaltCheckbox
                key={option.id}
                labelText={option.name}
                checked={selectedSet.has(String(option.id))}
                onChange={handleItemSelect(String(option.id))}
              />
            ))}
          </div>
        ) : (
          <span role="status" aria-live="polite">
            {search ? `No filters to display` : `No ${labelLower} to display`}
          </span>
        )}

        {hasMoreOptions && (
          <CobaltButton
            size="x-small"
            variant="ghost"
            className={styles.showMoreButton}
            onClick={handleToggleShowAll}
            aria-expanded={showAll}
            aria-controls={`${headingId}-checkbox-group`}
            aria-label={
              showAll ? `Show fewer ${labelLower}` : `Show more ${labelLower}`
            }
          >
            {showAll ? 'Show less' : 'Show more'}
          </CobaltButton>
        )}
      </section>
    );
  }
);

CheckboxFilterSection.displayName = 'CheckboxFilterSection';

export { CheckboxFilterSection };
